/**
 * A small initials avatar for whoever owns, shared or added something in the
 * Deal Room. The full name rides along as the tooltip and the accessible name.
 */
import React from "react";
import { initials } from "../../utils/folderSharing";

interface OwnerAvatarProps {
	name: string | null | undefined;
	/** Prefix for the accessible label, e.g. "Shared by". */
	label?: string;
	size?: "sm" | "md";
	/**
	 * Hidden from assistive tech and without its own tooltip: the caller names
	 * the person itself (a bubble group with one label for everyone).
	 */
	decorative?: boolean;
	/** Extra classes, e.g. a ring where bubbles overlap. */
	className?: string;
}

const SIZES: Record<NonNullable<OwnerAvatarProps["size"]>, string> = {
	sm: "h-5 w-5 text-[9px]",
	md: "h-7 w-7 text-[11px]",
};

const OwnerAvatar: React.FC<OwnerAvatarProps> = React.memo(
	({ name, label, size = "sm", decorative = false, className = "" }) => {
		const title = label
			? `${label} ${name || "a teammate"}`
			: name || undefined;
		const a11y = decorative
			? { "aria-hidden": true }
			: { title, "aria-label": title, role: "img" };
		return (
			<span
				{...a11y}
				className={`inline-flex flex-shrink-0 items-center justify-center rounded-full bg-[#004C4B] font-bold text-white ${SIZES[size]} ${className}`}
			>
				{initials(name)}
			</span>
		);
	},
);
OwnerAvatar.displayName = "OwnerAvatar";

export default OwnerAvatar;
